import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Typography,
  Grid,
  Card,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../utils/api";

const AddGateVisitorForm = ({ clubMemberCode, onSuccess }) => {
  const [formData, setFormData] = useState({
    ClubMemberID: clubMemberCode,
    FirstName: "",
    LastName: "",
    Description: "",
    ArrivalDate: "",
    DepartureDate: "",
    VehicleType: "",
    VehiclePlate: "",
    VisitorTypeID: "",
    Notes: "",
    PictureFile: null,
    AuthorizedBy: "",
    EnteredBy: "",
  });

  const [types, setTypes] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [plateSuggestions, setPlateSuggestions] = useState([]);
  const [blacklistWarning, setBlacklistWarning] = useState(null);

  useEffect(() => {
    if (clubMemberCode) {
      setFormData((prev) => ({ ...prev, ClubMemberID: clubMemberCode }));

      api
        .get(`/members/${clubMemberCode}`)
        .then((res) => {
          const fullName = `${res.data.FirstName?.trim() || ""} ${
            res.data.LastName?.trim() || ""
          }`.trim();

          setFormData((prev) => ({ ...prev, AuthorizedBy: fullName }));
        })
        .catch((err) => {
          console.error("Failed to load member info:", err);
        });
    }

    try {
      const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");
      const localUser = JSON.parse(localStorage.getItem("user") || "{}");
      const username =
        sessionUser?.Username ||
        sessionUser?.username ||
        localUser?.Username ||
        localUser?.username ||
        "";

      if (username) {
        setFormData((prev) => ({ ...prev, EnteredBy: String(username).trim() }));
      }
    } catch (err) {
      console.error("Failed to parse user info:", err);
    }
  }, [clubMemberCode]);

  useEffect(() => {
    const loadTypes = async () => {
      try {
        setLoadingTypes(true);
        const typeRes = await api.get("/gate-visitor-types");
        setTypes(typeRes.data || []);
      } catch (err) {
        console.error("Failed to load gate visitor types:", err);
        setTypes([]);
      } finally {
        setLoadingTypes(false);
      }
    };

    loadTypes();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  // Check blacklist when first/last name or plate change
  const checkBlacklist = useCallback(async (firstName, lastName, plate) => {
    if (!firstName && !lastName && !plate) { setBlacklistWarning(null); return; }
    try {
      const res = await api.get("/gate-blacklist/check", { params:{ firstName, lastName, plate } });
      setBlacklistWarning(res.data.isBlacklisted ? res.data.entry : null);
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      checkBlacklist(formData.FirstName, formData.LastName, formData.VehiclePlate);
    }, 600);
    return () => clearTimeout(t);
  }, [formData.FirstName, formData.LastName, formData.VehiclePlate, checkBlacklist]);

  // Plate autocomplete
  const handlePlateLookup = async (val) => {
    if (!val || val.length < 2) { setPlateSuggestions([]); return; }
    try {
      const res = await api.get("/gate-visitors/lookup/plate", { params:{ plate: val } });
      setPlateSuggestions(res.data || []);
    } catch { setPlateSuggestions([]); }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (file) {
      setFormData((f) => ({ ...f, PictureFile: file }));
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setFormData((f) => ({ ...f, PictureFile: null }));
      setPreviewUrl(null);
    }
  };

  const clearImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setFormData((f) => ({ ...f, PictureFile: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      const submission = new FormData();
      submission.append("ClubMemberID", String(clubMemberCode || "").trim());

      Object.entries(formData).forEach(([key, value]) => {
        if (key !== "ClubMemberID" && value !== null && value !== undefined && value !== "") {
          submission.append(key, value);
        }
      });

      await api.post("/gate-visitors", submission, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSnackbar({ open: true, message: "Visitor added successfully", severity: "success" });
      // Notify parent so GateVisitorsList can refresh immediately
      onSuccess?.();

      setFormData((prev) => ({
        ClubMemberID: clubMemberCode,
        FirstName: "",
        LastName: "",
        Description: "",
        ArrivalDate: "",
        DepartureDate: "",
        VehicleType: "",
        VehiclePlate: "",
        VisitorTypeID: "",
        Notes: "",
        PictureFile: null,
        AuthorizedBy: prev.AuthorizedBy,
        EnteredBy: prev.EnteredBy,
      }));

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
    } catch (err) {
      console.error("Error submitting visitor:", err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to add visitor.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const readonlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f7f9fc",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.5,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2.25,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  if (loadingTypes) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <PersonAddAlt1Icon sx={{ color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Add Gate Visitor
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Add a visitor record, visit details, vehicle information, and optional photo.
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}
        >
          <Grid container spacing={2.5}>
            {blacklistWarning && (
              <Grid item xs={12}>
                <Alert severity="error" icon={<WarningAmberIcon />} sx={{ borderRadius:2 }}>
                  <strong>Blacklist Warning:</strong> This visitor ({[blacklistWarning.FirstName, blacklistWarning.LastName].filter(Boolean).join(" ")}
                  {blacklistWarning.VehiclePlate ? ` / ${blacklistWarning.VehiclePlate}` : ""}) is on the blacklist.
                  {blacklistWarning.Reason ? ` Reason: ${blacklistWarning.Reason}` : ""}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                name="FirstName"
                label="First Name"
                fullWidth
                required
                value={formData.FirstName}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="LastName"
                label="Last Name"
                fullWidth
                required
                value={formData.LastName}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="Description"
                label="Description"
                fullWidth
                required
                value={formData.Description}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="ArrivalDate"
                label="Arrival Date/Time"
                type="datetime-local"
                fullWidth
                required
                value={formData.ArrivalDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="DepartureDate"
                label="Departure Date/Time"
                type="datetime-local"
                fullWidth
                value={formData.DepartureDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="VehicleType"
                label="Vehicle Type"
                fullWidth
                value={formData.VehicleType}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={plateSuggestions}
                getOptionLabel={(o) => typeof o === "string" ? o : `${o.VehiclePlate}${o.FirstName ? ` (${o.FirstName} ${o.LastName})` : ""}`}
                inputValue={formData.VehiclePlate}
                onInputChange={(_, val) => {
                  setFormData(f => ({ ...f, VehiclePlate: val }));
                  handlePlateLookup(val);
                }}
                onChange={(_, selected) => {
                  if (selected && typeof selected === "object") {
                    setFormData(f => ({
                      ...f,
                      VehiclePlate: selected.VehiclePlate || "",
                      VehicleType: selected.VehicleType || f.VehicleType,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} name="VehiclePlate" label="Vehicle Plate" fullWidth sx={textFieldSx} />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="VisitorTypeID"
                label="Visitor Type"
                fullWidth
                select
                value={formData.VisitorTypeID}
                onChange={handleChange}
                sx={textFieldSx}
              >
                {types.map((t) => (
                  <MenuItem key={t.TypeID} value={t.TypeID}>
                    {t.TypeName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="Notes"
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={formData.Notes}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="AuthorizedBy"
                label="Authorized By"
                fullWidth
                required
                value={formData.AuthorizedBy}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="EnteredBy"
                label="Entered By"
                fullWidth
                value={formData.EnteredBy}
                InputProps={{ readOnly: true }}
                sx={readonlyFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: "#6b778c", mb: 1 }}>
                Visitor Photo
              </Typography>

              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={secondaryButtonSx}
              >
                Upload Visitor Photo
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={handleImageChange}
                />
              </Button>
            </Grid>

            {previewUrl && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "1px solid rgba(47,99,173,0.08)",
                    borderRadius: "16px",
                    p: 1.5,
                    backgroundColor: "rgba(47,99,173,0.02)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, color: "#223142" }}>
                      Image Preview
                    </Typography>

                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={clearImage}
                      sx={{
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Remove
                    </Button>
                  </Box>

                  <Box
                    component="img"
                    src={previewUrl}
                    alt="Visitor Preview"
                    sx={{
                      width: "100%",
                      maxHeight: 280,
                      objectFit: "contain",
                      borderRadius: "12px",
                      border: "1px solid rgba(47,99,173,0.08)",
                      backgroundColor: "#fff",
                    }}
                  />
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                  sx={primaryButtonSx}
                >
                  {isSubmitting ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Submit Visitor"
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddGateVisitorForm;
